<template>
  <b-card >
    <h3>Histórico de pré-análise:</h3>

    <div >
      <b-table
        :items="history"
        :fields="fields"
        responsive
        hover
        show-empty
        empty-text="Nenhum histórico encontrado"
      >
        <template #cell(analysisDate)="data">
          {{ formatDate(data.item.analysisDate) }}
        </template>

        <template #cell(step)="data">
          {{ formatStep(data.item.step) }}
        </template>

        <template #cell(status)="data">
          <span
            class="status-badge"
            :class="getStatusClass(data.item.status)"
          >
            {{ data.item.status }}
          </span>
        </template>

        <template #cell(error)="data">
          <feather-icon
            v-if="hasError(data.item.error)"
            icon="XCircleIcon"
            size="24"
            class="error-icon"
            title="Erro na etapa"
          />
          <feather-icon
            v-else
            icon="CheckCircleIcon"
            size="24"
            class="success-icon"
            title="Sem erro na etapa"
          />
        </template>

        <template #cell(reason)="data">
          <ul
            v-if="data.item.reason && data.item.reason.length"
            class="reason-list"
          >
            <li
              v-for="(reason, index) in data.item.reason"
              :key="index"
            >
              {{ reason }}
            </li>
          </ul>

          <span v-else>-</span>
        </template>
      </b-table>
    </div>

    <div
      v-if="hasPendingSteps"
      class="d-flex justify-content-center mt-4"
    >
      <b-button
        variant="primary"
        :disabled="isReprocessing"
        @click="$emit('reprocess-pending')"
      >
        <span v-if="isReprocessing" class="reprocessing-spinner mr-1"></span>
        <template v-else>
          <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
        </template>
        {{ isReprocessing ? 'Reprocessando...' : 'Reprocessar etapas pendentes' }}
      </b-button>
    </div>

    <div
      v-else-if="exceptionRequest && exceptionRequest.file"
    >

     <div class="d-flex justify-content-center align-items-center gap-3">
      <b-button
        variant="light"
        class="view-button mr-1"
        :disabled="isViewing"
        @click="viewFile"
      >
        <div v-if="isViewing" class="dotted-spinner"></div>

        <template v-else>
          <feather-icon
            icon="EyeIcon"
            size="16"
          />
          Visualizar arquivo
        </template>
      </b-button>

      <b-button
        variant="light"
        class="download-button mr-1"
        :disabled="isDownloading"
        @click="downloadFile"
      >
        <div v-if="isDownloading" class="dotted-spinner"></div>

        <template v-else>
          <feather-icon
            icon="DownloadIcon"
            size="16"
          />
          Baixar arquivo
        </template>
      </b-button>

      <b-button
        v-if="canReplaceFile"
        variant="light"
        class="replace-button"
        @click="$emit('replace-file')"
      >
      <feather-icon
          icon="UploadIcon"
          size="16"
      />
        Alterar arquivo
      </b-button>
    </div>

    <b-modal
      v-model="showFileModal"
      title="Visualizar arquivo"
      size="xl"
      hide-footer
      centered
    >
      <div class="file-preview">
        <iframe
          v-if="filePreviewUrl"
          :src="filePreviewUrl"
          width="100%"
          height="700"
          frameborder="0"
        />
      </div>
    </b-modal>
    </div>

    <div
      v-else-if="canRequestException"
      class=" d-flex justify-content-center mt-4"
    >
      <b-button
        variant="primary"
        @click="$emit('request-exception')"
      >
        Solicitar aprovação por Exceção
      </b-button>
    </div>
  </b-card>
</template>

<script>
import { BCard, BTable, BButton, BModal } from 'bootstrap-vue'
import CreditAnalysisService from '@/service/credit-analysis'

export default {
  name: 'PreAnalysisHistory',

  components: {
    BCard,
    BTable,
    BButton,
    BModal,
  },

  props: {
    history: {
      type: Array,
      default: () => []
    },

    exceptionRequest: {
      type: Object,
      default: null
    },
    analysisId: {
      type: [Number, String],
      required: true
    },
    isReprocessing: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      fields: [
        { key: 'analysisDate', label: 'Data da Análise' },
        { key: 'step', label: 'Etapa' },
        { key: 'status', label: 'Status da etapa' },
        { key: 'reason', label: 'Motivo' },
        { key: 'error', label: 'Executado', thClass: 'text-center', tdClass: 'text-center' },
      ],
      showFileModal: false,
      filePreviewUrl: null,
      isDownloading: false,
      isViewing: false
    }
  },

  computed: {
    hasPendingSteps() {
      return this.history.some(step => (
        this.hasError(step.error) 
      ))
    },

    canReplaceFile() {
      const uploadedAt = this.exceptionRequest
        && this.exceptionRequest.file
        && this.exceptionRequest.file.uploadedAt

      return uploadedAt
        ? Date.now() - new Date(uploadedAt).getTime() < 24 * 60 * 60 * 1000
        : false
    },

    canRequestException() {
      if (!this.history.length) {
        return true
      }

      const lastHistory = this.history[this.history.length - 1]

      return lastHistory.status !== 'Aprovada'
    }
  },

  methods: {
    hasError(error) {
      return error === 1 
    },

    formatDate(date) {
      if (!date) {
        return '-'
      }

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date))
    },

    formatStep(step) {
      const steps = {
        Blacklist: 'Blacklist',
        InternalHistory: 'Histórico Interno',
        Beecred: 'Beecred',
        Process: 'Processos',
        Pgfn: 'PGFN',
        Pge: 'PGE',
        Serasa: 'Serasa',
        Scr: 'SCR',
        Finished: 'Finalizado'
      }

      return steps[step] || step
    },

    getStatusClass(status) {
      if (status === 'Reprovada') {
        return 'status-rejected'
      }

      if (status === 'Aprovada') {
        return 'status-approved'
      }

      if (status === 'Aprovada com exceção') {
        return 'status-exception'
      }

      if (status === 'Em análise') {
        return 'status-pending'
      }

      return ''
    },

    async downloadFile() {
      if (!this.analysisId) {
        this.$toast.error('Análise não encontrada.')
        return
      }

      this.isDownloading = true

      try {
        const response = await CreditAnalysisService.downloadFile(this.analysisId)
        const payload = response && response.data

        if (payload instanceof Blob) {
          const blobUrl = window.URL.createObjectURL(payload)
          const link = document.createElement('a')

          link.href = blobUrl
          link.download = this.exceptionRequest.file.name || 'arquivo-de-excecao.pdf'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
          return
        }

        const fileUrl = typeof payload === 'string'
          ? payload
          : payload && (payload.url || payload.downloadUrl)

        if (!fileUrl) {
          throw new Error('URL do arquivo não encontrada.')
        }

        const link = document.createElement('a')

        link.href = fileUrl
        link.download = this.exceptionRequest.file.name || 'arquivo-de-excecao.pdf'
        link.target = '_blank'
        link.rel = 'noopener noreferrer'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        this.$toast.error('Não foi possível baixar o arquivo.')
      }
      finally {
        this.isDownloading = false
      }
    }, 

   async viewFile() {
      if (!this.analysisId) {
        this.$toast.error('Análise não encontrada.')
        return
      }

      this.isViewing = true

      try {
        const response = await CreditAnalysisService.previewFile(this.analysisId)
        const payload = response && response.data

        const fileUrl = typeof payload === 'string'
          ? payload
          : payload && (payload.url || payload.previewUrl)

        if (!fileUrl) {
          throw new Error('URL do arquivo não encontrada.')
        }

        this.filePreviewUrl = fileUrl
        this.showFileModal = true
      } catch (error) {
        this.$toast.error('Não foi possível visualizar o arquivo.')
      } finally {
        this.isViewing = false
      }
    },
  }
}
</script>

<style scoped>
.error-icon {
  color: #ea5455;
}

.success-icon {
  color: #28c76f;
}

.dotted-spinner {
  width: 18px;
  height: 18px;
  border: 3px dotted currentColor;
  border-radius: 50%;
  color: var(--primary);
  animation: spin 1.5s linear infinite;
}

.reprocessing-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  vertical-align: middle;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>